import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { translateConfigValue } from '@/utils/translateConfigValue';
import { useEffect, useRef, useState } from 'react';

interface VisibleElement {
  selector: string;
  ratio: number;
  topPosition: number; // Position relative to viewport top
  visibleArea: number; // Visible area in pixels
}

export const useContextSuggestions = () => {
  const { config } = useConfigStore();
  const addSeenContext = useChatStore(state => state.addSeenContext);
  const [currentSuggestion, setCurrentSuggestion] = useState<{
    text: string;
    chatQuestion?: string;
    quickAnswer?: string;
  } | null>(null);
  const language = config.language ?? 'de';

  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsMapRef = useRef<Map<string, Element>>(new Map());
  const visibleElementsRef = useRef<Map<string, VisibleElement>>(new Map());
  const lastVisibleSelectorRef = useRef<string | null>(null);
  const frameRef = useRef<number | null>(null);
  const prevSuggestionRef = useRef<{
    text: string;
    chatQuestion?: string;
    quickAnswer?: string;
  } | null>(null);
  const addSeenContextRef = useRef(addSeenContext);
  const updateCurrentSuggestionRef = useRef<(() => void) | null>(null);

  // Keep latest addSeenContext without re-subscribing the effect
  useEffect(() => {
    addSeenContextRef.current = addSeenContext;
  }, [addSeenContext]);

  // Helper function to calculate visible metrics
  const calculateVisibility = (element: Element): VisibleElement | null => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check if element is in viewport
    const isInViewport =
      rect.bottom >= 0 &&
      rect.top <= viewportHeight &&
      rect.right >= 0 &&
      rect.left <= viewportWidth;

    if (!isInViewport) {
      return null;
    }

    // Calculate visible area
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleLeft = Math.max(0, rect.left);
    const visibleRight = Math.min(viewportWidth, rect.right);

    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const visibleArea = visibleHeight * visibleWidth;

    // Calculate intersection ratio (how much of element is visible)
    const elementArea = rect.height * rect.width;
    const ratio = elementArea > 0 ? visibleArea / elementArea : 0;

    return {
      selector: '', // Will be set by caller
      ratio,
      topPosition: rect.top,
      visibleArea,
    };
  };

  // Function to determine the most relevant visible element
  // Simple approach: select the highest element on screen among all visible elements
  // Exception: if page is scrolled to bottom (<50px remaining), select the lowest element
  const getMostRelevantElement = (): VisibleElement | null => {
    if (visibleElementsRef.current.size === 0) {
      return null;
    }

    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if page is scrolled to bottom (less than 50px remaining)
    const isNearBottom = viewportHeight + scrollY >= documentHeight - 50;

    let mostRelevant: VisibleElement | null = null;

    visibleElementsRef.current.forEach(element => {
      // Get the actual element to check its bounds
      const elementNode = elementsMapRef.current.get(element.selector);
      if (!elementNode) return;

      const rect = elementNode.getBoundingClientRect();

      // Element is considered visible on screen if:
      // 1. Its bottom edge is below the top of viewport (not completely above)
      // 2. Its top edge is above the bottom of viewport (not completely below)
      // 3. At least 10% of the element is visible
      const isVisibleOnScreen =
        rect.bottom > 0 && // Element is not completely above viewport
        rect.top < viewportHeight && // Element is not completely below viewport
        element.ratio > 0.1; // At least 10% visible

      if (!isVisibleOnScreen) {
        return;
      }

      // If page is near bottom, select the LOWEST element (highest topPosition)
      // Otherwise, select the HIGHEST element (lowest topPosition)
      if (!mostRelevant) {
        mostRelevant = element;
      } else if (isNearBottom) {
        // Near bottom: prefer element with higher topPosition (lower on screen)
        if (element.topPosition > mostRelevant.topPosition) {
          mostRelevant = element;
        }
      } else {
        // Normal scroll: prefer element with lower topPosition (higher on screen)
        if (element.topPosition < mostRelevant.topPosition) {
          mostRelevant = element;
        }
      }
    });

    return mostRelevant;
  };

  // Initialize intersection observer and find elements by selectors
  useEffect(() => {
    // Function to update current suggestion based on visible elements
    const updateCurrentSuggestion = () => {
      const mostRelevant = getMostRelevantElement();

      if (!mostRelevant || !config?.contextSuggestions) {
        if (prevSuggestionRef.current !== null) {
          prevSuggestionRef.current = null;
          setCurrentSuggestion(null);
        }
        return;
      }

      const selector = mostRelevant.selector;
      const suggestion = config.contextSuggestions[selector];

      if (!suggestion) {
        if (prevSuggestionRef.current !== null) {
          prevSuggestionRef.current = null;
          setCurrentSuggestion(null);
        }
        return;
      }

      // Only add to seen contexts if this is a new selector
      if (lastVisibleSelectorRef.current !== selector) {
        lastVisibleSelectorRef.current = selector;
        if (suggestion.description) {
          const description = translateConfigValue(suggestion.description, language);
          addSeenContextRef.current(selector, description);
        }
      }

      // Build next suggestion and only update if it actually changed
      if (suggestion.suggestion) {
        const text = translateConfigValue(suggestion.suggestion, language);
        const chatQuestion = suggestion.chatQuestion
          ? translateConfigValue(suggestion.chatQuestion, language)
          : undefined;
        const quickAnswer = suggestion.quickAnswer
          ? translateConfigValue(suggestion.quickAnswer, language)
          : undefined;

        if (text) {
          const next = {
            text,
            chatQuestion: chatQuestion ?? undefined,
            quickAnswer: quickAnswer ?? undefined,
          } as const;

          const prev = prevSuggestionRef.current;
          const isSame =
            prev?.text === next.text &&
            prev?.chatQuestion === next.chatQuestion &&
            prev?.quickAnswer === next.quickAnswer;

          if (!isSame) {
            prevSuggestionRef.current = next;
            setCurrentSuggestion(next);
          }
        } else if (prevSuggestionRef.current !== null) {
          prevSuggestionRef.current = null;
          setCurrentSuggestion(null);
        }
      } else if (prevSuggestionRef.current !== null) {
        prevSuggestionRef.current = null;
        setCurrentSuggestion(null);
      }
    };

    // Store update function in ref for use in rescanElements
    updateCurrentSuggestionRef.current = updateCurrentSuggestion;

    if (!config?.contextSuggestions) {
      setCurrentSuggestion(null);
      return;
    }

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      elementsMapRef.current.clear();
      visibleElementsRef.current.clear();
    }

    // Find all elements by selectors
    const foundElements = new Map<string, Element>();

    Object.keys(config.contextSuggestions).forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        // Use the first found element for each selector
        if (elements.length > 0) {
          foundElements.set(selector, elements[0]);
        }
      } catch (error) {
        console.warn(`Invalid CSS selector: ${selector}`, error);
      }
    });

    if (foundElements.size === 0) {
      setCurrentSuggestion(null);
      return;
    }

    // Store elements map
    elementsMapRef.current = foundElements;

    // Create intersection observer with multiple thresholds for better detection
    const observer = new IntersectionObserver(
      entries => {
        // Update visible elements map based on entries
        entries.forEach(entry => {
          // Find the selector for this element
          let matchingSelector = '';
          for (const [selector, element] of elementsMapRef.current.entries()) {
            if (element === entry.target) {
              matchingSelector = selector;
              break;
            }
          }

          if (matchingSelector) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
              // Element is visible - calculate and store visibility metrics
              const visibility = calculateVisibility(entry.target);
              if (visibility) {
                visibility.selector = matchingSelector;
                visibleElementsRef.current.set(matchingSelector, visibility);
              }
            } else {
              // Element is not visible - remove from visible map
              visibleElementsRef.current.delete(matchingSelector);
            }
          }
        });

        // Debounce to once per frame to avoid rapid state churn
        if (frameRef.current !== null) {
          cancelAnimationFrame(frameRef.current);
        }
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          updateCurrentSuggestion();
        });
      },
      {
        // Use multiple thresholds for better detection of small sections
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px', // Remove negative margin to detect sections better
      }
    );

    observerRef.current = observer;

    // Observe all found elements
    foundElements.forEach(element => {
      observer.observe(element);
    });

    // Initial check for already visible elements
    const checkInitialVisibility = () => {
      foundElements.forEach((element, selector) => {
        const visibility = calculateVisibility(element);
        if (visibility && visibility.ratio > 0.1) {
          visibility.selector = selector;
          visibleElementsRef.current.set(selector, visibility);
        }
      });
      updateCurrentSuggestion();
    };

    // Check immediately and after a short delay
    checkInitialVisibility();
    const initialTimer = setTimeout(checkInitialVisibility, 100);

    // Store refs in local variables for cleanup
    const elementsMap = elementsMapRef.current;
    const visibleElements = visibleElementsRef.current;

    return () => {
      clearTimeout(initialTimer);
      observer.disconnect();
      elementsMap.clear();
      visibleElements.clear();
      observerRef.current = null;
      lastVisibleSelectorRef.current = null;
      updateCurrentSuggestionRef.current = null;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [config?.contextSuggestions, language]);

  // Re-scan for elements when DOM might have changed
  const rescanElements = () => {
    if (!config?.contextSuggestions) return;

    const observer = observerRef.current;
    if (observer) {
      observer.disconnect();
      elementsMapRef.current.clear();
      visibleElementsRef.current.clear();

      // Re-find elements
      const foundElements = new Map<string, Element>();

      Object.keys(config.contextSuggestions).forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          if (elements.length > 0) {
            foundElements.set(selector, elements[0]);
          }
        } catch (error) {
          console.warn(`Invalid CSS selector: ${selector}`, error);
        }
      });

      elementsMapRef.current = foundElements;

      // Re-observe elements
      foundElements.forEach(element => {
        observer.observe(element);
      });

      // Re-check visibility
      foundElements.forEach((element, selector) => {
        const visibility = calculateVisibility(element);
        if (visibility && visibility.ratio > 0.1) {
          visibility.selector = selector;
          visibleElementsRef.current.set(selector, visibility);
        }
      });

      // Update suggestion after rescan using stored function
      if (updateCurrentSuggestionRef.current) {
        updateCurrentSuggestionRef.current();
      }
    }
  };

  return {
    currentSuggestion,
    rescanElements,
  };
};
